import React, { useState, useEffect, useCallback } from 'react';
import { 
  useCreateCourseMutation, 
  usePublishCourseMutation, 
  useUpdateCourseMutation,
  useGetCourseDetailQuery 
} from '../redux/apiSlice'; 
import { useLocation, useNavigate } from 'react-router-dom';
import ProgressSteps from '../components/ProgressSteps';
import BasicInfoStep from '../components/BasicInfoStep';
import ContentStep from '../components/ContentStep';
import MediaPricingStep from '../components/MediaPricingStep';
import SettingsStep from '../components/SettingsStep';

const CreateCourse = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [submittedSteps, setSubmittedSteps] = useState(new Set());
  const [dataLoaded, setDataLoaded] = useState(false);
  const [originalThumbnailUrl, setOriginalThumbnailUrl] = useState(null);
  const [originalCourseData, setOriginalCourseData] = useState(null);
  const [originalSectionsData, setOriginalSectionsData] = useState([]);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const [createCourse, { isLoading: isCreatingCourse }] = useCreateCourseMutation();
  const [publishCourse, { isLoading: isPublishingCourse }] = usePublishCourseMutation();
  const [updateCourse, { isLoading: isUpdatingCourse }] = useUpdateCourseMutation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editCourseId = urlParams.get('edit');
    
    if (editCourseId) {
      setIsEditMode(true);
      setDraftId(editCourseId);
      setDataLoaded(false);
      console.log('🔄 Edit mode activated for course:', editCourseId);
    }
  }, [location.search]);

  const { 
    data: courseDetailData, 
    isLoading: isCourseDetailLoading,
    isError: isCourseDetailError,
    error: courseDetailError,
    refetch
  } = useGetCourseDetailQuery(draftId, {
    skip: !draftId || !isEditMode,
    refetchOnMountOrArgChange: true
  });

  const extractArrayData = (field, course) => {
    if (!course || !course[field]) return [''];
    
    console.log(`🔍 Extracting ${field} from API:`, course[field]);
    console.log(`🔍 Type of ${field}:`, typeof course[field]);
    
    if (Array.isArray(course[field])) {
      if (course[field].length === 0) {
        console.log(`⚠️ ${field} array is empty`);
        return [''];
      }
      
      console.log(`🔍 ${field} array items:`, course[field]);
      
      const result = course[field]
        .map(item => {
          if (typeof item === 'object' && item !== null) {
            if (item.content && typeof item.content === 'string') {
              return item.content.trim();
            }
            if (item.text && typeof item.text === 'string') {
              return item.text.trim();
            }
            if (item.title && typeof item.title === 'string') {
              return item.title.trim();
            }
            if (item.name && typeof item.name === 'string') {
              return item.name.trim();
            }
            return JSON.stringify(item).trim();
          }
          if (typeof item === 'string') {
            return item.trim();
          }
          return String(item).trim();
        })
        .filter(item => item && item.trim());
      
      console.log(`✅ Extracted ${field}:`, result);
      return result.length > 0 ? result : [''];
    }
    
    if (typeof course[field] === 'string') {
      const result = course[field]
        .split(',')
        .map(item => item.trim())
        .filter(item => item);
      console.log(`✅ Split string ${field}:`, result);
      return result.length > 0 ? result : [''];
    }
    
    return [''];
  };

  // ✅ FIXED: Recursive function to transform API sections data to frontend format
  const transformSectionsData = (sections) => {
    if (!sections || !Array.isArray(sections)) return [];
    
    const transformSingleSection = (section) => {
      console.log('🔍 Transforming section:', section);
      console.log('🔍 Section items:', section.items);
      
      // Extract videos, pdfs, and quizzes from items array
      const videos = [];
      const pdfs = [];
      const quizzes = [];
      
      if (section.items && Array.isArray(section.items)) {
        section.items.forEach(item => {
          console.log('🔍 Processing item:', item);
          console.log('🔍 Item type:', item.type);
          
          if (item.type === 'VIDEO' && item.video) {
            console.log('🎬 Processing video item:', item.video);
            
            const videoData = {
              id: item.video.id,
              title: item.video.title,
              description: item.video.description,
              vimeoUrl: item.video.vimeoUrl,
              duration: item.video.duration,
              serverVideoId: item.video.id,
              uploadStatus: 'success',
              uploadProgress: 100,
              isLocked: item.islock || true,
              hasPdf: item.video.pdfs && item.video.pdfs.length > 0 ? true : false,
              ...(item.video.pdfs && item.video.pdfs.length > 0 && {
                serverPdfUrl: item.video.pdfs[0]?.fileUrl,
                serverPdfId: item.video.pdfs[0]?.id,
                pdfUploadStatus: 'success'
              })
            };
            
            // ✅ Check if video has attached quizzes in InstructorQuiz array
            if (item.video.InstructorQuiz && Array.isArray(item.video.InstructorQuiz) && item.video.InstructorQuiz.length > 0) {
              console.log('🎯 Video has quizzes in InstructorQuiz:', item.video.InstructorQuiz);
              
              item.video.InstructorQuiz.forEach(quiz => {
                console.log('📝 Processing video quiz from InstructorQuiz:', quiz);
                
                const quizData = {
                  id: quiz.id,
                  title: quiz.title,
                  isActive: true,
                  type: 'video',
                  videoId: item.video.id,
                  questions: []
                };
                
                // ✅ Transform quiz questions if they exist
                if (quiz.questions && Array.isArray(quiz.questions)) {
                  console.log('📝 Video quiz has questions:', quiz.questions);
                  quizData.questions = quiz.questions.map((q) => {
                    // Format options properly
                    const options = q.options ? q.options.map(opt => ({
                      id: opt.id,
                      text: opt.text,
                      isCorrect: opt.isCorrect
                    })) : [
                      { id: 1, text: 'Option 1', isCorrect: false },
                      { id: 2, text: 'Option 2', isCorrect: false },
                      { id: 3, text: 'Option 3', isCorrect: false },
                      { id: 4, text: 'Option 4', isCorrect: false }
                    ];
                    
                    return {
                      id: q.id,
                      question: q.question,
                      options: options,
                      correctAnswer: options.find(opt => opt.isCorrect)?.text || '',
                      explanation: q.explanation || '',
                      marks: q.marks || 1
                    };
                  });
                }
                
                quizzes.push(quizData);
                console.log('✅ Added video quiz from InstructorQuiz:', quizData);
              });
            }
            
            videos.push(videoData);
            console.log('✅ Added video:', videoData);
          }
          
          if (item.type === 'PDF' && item.pdf) {
            const pdfData = {
              id: item.pdf.id,
              title: item.pdf.fileName,
              serverPdfUrl: item.pdf.fileUrl,
              serverPdfId: item.pdf.id,
              uploadStatus: 'success',
              isLocked: item.islock || true,
              isStandalone: true
            };
            pdfs.push(pdfData);
            console.log('✅ Added PDF:', pdfData);
          }
          
          if (item.type === 'QUIZ' && item.quiz) {
            console.log('❓ Processing standalone quiz item:', item.quiz);
            
            // Format quiz questions properly
            const formattedQuestions = [];
            if (item.quiz.questions && Array.isArray(item.quiz.questions)) {
              item.quiz.questions.forEach(q => {
                // Format options
                const formattedOptions = [];
                if (q.options && Array.isArray(q.options)) {
                  q.options.forEach(opt => {
                    formattedOptions.push({
                      id: opt.id,
                      text: opt.text,
                      isCorrect: opt.isCorrect
                    });
                  });
                }
                
                formattedQuestions.push({
                  id: q.id,
                  question: q.question,
                  options: formattedOptions,
                  text: q.question // For backward compatibility
                });
              });
            }
            
            const quizData = {
              id: item.quiz.id,
              title: item.quiz.title || 'Untitled Quiz',
              questions: formattedQuestions,
              totalQuestions: formattedQuestions.length,
              isLocked: item.islock || item.quiz.islock || false,
              status: item.islock ? 'locked' : 'unlocked',
              sectionId: section.id,
              createdAt: item.quiz.createdAt || new Date().toISOString(),
              type: 'section'
            };
            
            quizzes.push(quizData);
            console.log('✅ Added standalone quiz:', quizData);
          }
        });
      } else {
        console.log('⚠️ Section has no items or items is not an array');
      }
      
      // ✅ Recursively transform child sections
      const nestedSections = section.children && Array.isArray(section.children) 
        ? section.children.map(childSection => transformSingleSection(childSection))
        : [];
      
      const transformedSection = {
        id: section.id,
        name: section.name,
        serverSectionId: section.id,
        videos: videos,
        pdfs: pdfs,
        quizzes: quizzes,
        nestedSections: nestedSections,
        deleteStatus: null
      };
      
      console.log('✅ Transformed section:', transformedSection);
      console.log('✅ Number of videos in section:', transformedSection.videos.length);
      console.log('✅ Number of PDFs in section:', transformedSection.pdfs.length);
      console.log('✅ Number of quizzes in section:', transformedSection.quizzes.length);
      console.log('✅ Number of nested sections:', nestedSections.length);
      
      return transformedSection;
    };
    
    // Transform all top-level sections
    const transformed = sections.map(section => transformSingleSection(section));
    
    console.log('📊 Final transformed sections:', transformed);
    console.log('📊 Total sections transformed:', transformed.length);
    
    return transformed;
  };

  useEffect(() => {
    if (isEditMode && courseDetailData?.success && courseDetailData?.course && !dataLoaded) {
      console.log('📥 Loading course data for editing:', courseDetailData.course);
      const course = courseDetailData.course;
      
      console.log('📊 Full course data from API:', course);
      
      let categoryId = '';
      let subCategoryId = '';
      
      if (course.category && typeof course.category === 'object') {
        categoryId = course.category.id?.toString() || '';
      } else if (course.categoryId) {
        categoryId = course.categoryId.toString();
      } else if (course.category) {
        categoryId = course.category.toString();
      }
      
      if (course.subCategory && typeof course.subCategory === 'object') {
        subCategoryId = course.subCategory.id?.toString() || '';
      } else if (course.subcategoryId) {
        subCategoryId = course.subcategoryId.toString();
      } else if (course.subCategory) {
        subCategoryId = course.subCategory.toString();
      }
      
      console.log('🔢 Extracted Category ID:', categoryId);
      console.log('🔢 Extracted Subcategory ID:', subCategoryId);
      
      const level = course.level || 'Beginner';
      console.log('🎯 Course level:', level);
      
      // ✅ Extract all overview data
      const objectives = extractArrayData('objectives', course);
      const requirements = extractArrayData('requirements', course);
      const audiences = extractArrayData('audiences', course);
      
      console.log('📝 Final objectives:', objectives);
      console.log('📝 Final requirements:', requirements);
      console.log('📝 Final audiences:', audiences);
      
      // ✅ Store original thumbnail URL
      if (course.thumbnailUrl) {
        setOriginalThumbnailUrl(course.thumbnailUrl);
        console.log('🖼️ Original thumbnail URL saved:', course.thumbnailUrl);
      }
      
      // ✅ Store original course data for comparison
      setOriginalCourseData({
        thumbnailUrl: course.thumbnailUrl,
        sections: course.sections || []
      });
      
      // ✅ Store original sections data in its raw format for sending in update
      setOriginalSectionsData(course.sections || []);
      console.log('📦 Original sections data saved for update:', course.sections);
      
      // ✅ Transform sections data from API format to frontend format
      const transformedSections = transformSectionsData(course.sections);
      console.log('✅ Transformed sections:', transformedSections);
      
      const formattedCourseData = {
        id: course.id,
        title: course.title || '',
        subtitle: course.subtitle || '',
        description: course.description || '',
        category: categoryId,
        subCategory: subCategoryId,
        level: level,
        language: course.language || 'English',
        // ✅ Overview fields
        objectives: objectives,
        requirements: requirements,
        audiences: audiences,
        price: course.price?.toString() || '',
        promoPrice: course.promoPrice?.toString() || '',
        thumbnail: course.thumbnailUrl || null,
        previewVideo: null,
        sections: transformedSections,
        isPublished: course.status === 'PUBLISHED',
        allowReviews: true,
        certificateIncluded: true,
        lifetimeAccess: true,
        supportIncluded: true,
        courseId: course.id
      };

      console.log('✅ Course data loaded for editing:', formattedCourseData);

      setCourseToEdit(formattedCourseData);
      setCourseData(formattedCourseData);
      setDataLoaded(true);
      
      // ✅ Mark all steps as submitted since we're in edit mode
      setSubmittedSteps(new Set([1, 2, 3]));
    }
  }, [isEditMode, courseDetailData, dataLoaded]);

  const initialCourseData = {
    id: null,
    title: '',
    subtitle: '',
    description: '',
    category: '',
    subCategory: '',
    level: 'Beginner',
    language: 'English',
    // ✅ Overview fields
    objectives: [''],
    requirements: [''],
    audiences: [''],
    price: '',
    promoPrice: '',
    thumbnail: null,
    previewVideo: null,
    sections: [],
    isPublished: false,
    allowReviews: true,
    certificateIncluded: true,
    lifetimeAccess: true,
    supportIncluded: true,
    courseId: null
  };

  const [courseData, setCourseData] = useState(initialCourseData);

  // Update courseData when data is loaded in edit mode
  useEffect(() => {
    if (isEditMode && courseToEdit) {
      console.log('🔄 Setting course data from loaded data:', courseToEdit);
      
      const updatedCourseToEdit = {
        ...courseToEdit,
        sections: courseToEdit.sections || []
      };
      
      setCourseData(updatedCourseToEdit);
    }
  }, [isEditMode, courseToEdit]);

  const steps = [
    { number: 1, title: 'Basic Info', description: 'Course details' },
    { number: 2, title: 'Content', description: 'What students will learn, Requirements & Audience' },
    { number: 3, title: 'Media & Pricing', description: 'Pricing & media' },
    { number: 4, title: 'Settings', description: 'Course settings' }
  ];

  const getApiStepId = (step) => {
    switch(step) {
      case 1: return 0;
      case 2: return 1;
      case 3: return 2;
      default: return 0;
    }
  };

  useEffect(() => {
    if (isEditMode) return;
    
    const savedDraft = localStorage.getItem('courseDraft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        if (draftData.id) {
          setDraftId(draftData.id);
        }
        
        const fixedDraftData = {
          ...initialCourseData,
          ...draftData,
          objectives: Array.isArray(draftData.objectives) ? draftData.objectives : [''],
          requirements: Array.isArray(draftData.requirements) ? draftData.requirements : [''],
          audiences: Array.isArray(draftData.audiences) ? draftData.audiences : [''],
          sections: Array.isArray(draftData.sections) ? draftData.sections : [],
        };
        
        setCourseData(fixedDraftData);
        
        const hasMeaningfulData = fixedDraftData.title || 
                                 fixedDraftData.description || 
                                 (fixedDraftData.objectives && fixedDraftData.objectives.some(obj => obj && obj.trim())) ||
                                 fixedDraftData.price ||
                                 fixedDraftData.thumbnail;
        
        if (hasMeaningfulData) {
          setShowDraftModal(true);
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    
    try {
      localStorage.setItem('courseDraft', JSON.stringify(courseData));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [courseData, isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    
    const handleBeforeUnload = (event) => {
      try {
        localStorage.setItem('courseDraft', JSON.stringify(courseData));
      } catch (error) {
        console.error('Error saving on page unload:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [courseData, isEditMode]);

  useEffect(() => {
    setIsSubmitting(false);
  }, [currentStep]);

  const handleInputChange = (field, value) => {
    setCourseData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setCourseData(prev => {
      const currentArray = Array.isArray(prev[field]) ? [...prev[field]] : [''];
      const updatedArray = [...currentArray];
      updatedArray[index] = value;
      
      const filteredArray = updatedArray.filter((item, i) => 
        item.trim() !== '' || i === updatedArray.length - 1
      );
      
      return {
        ...prev,
        [field]: filteredArray
      };
    });
  };

  const addArrayField = (field) => {
    setCourseData(prev => {
      const currentArray = Array.isArray(prev[field]) ? [...prev[field]] : [''];
      
      if (currentArray.length === 0 || currentArray[currentArray.length - 1].trim() !== '') {
        return {
          ...prev,
          [field]: [...currentArray, '']
        };
      }
      
      return prev;
    });
  };

  const removeArrayField = (field, index) => {
    setCourseData(prev => {
      const currentArray = Array.isArray(prev[field]) ? [...prev[field]] : [''];
      const updatedArray = currentArray.filter((_, i) => i !== index);
      if (updatedArray.length === 0) {
        updatedArray.push('');
      }
      return {
        ...prev,
        [field]: updatedArray
      };
    });
  };

  const handleFileUpload = (field, file, previewUrl) => {
    if (field === 'thumbnail') {
      setThumbnailFile(file);
      handleInputChange('thumbnail', previewUrl);
    } else {
      handleInputChange(field, URL.createObjectURL(file));
    }
  };

  const handleThumbnailChange = (file, previewUrl) => {
    setThumbnailFile(file);
    handleInputChange('thumbnail', previewUrl);
  };

  const handleComplexArrayFieldChange = (field, index, updatedItem) => {
    setCourseData(prev => {
      const currentArray = Array.isArray(prev[field]) ? [...prev[field]] : [];
      const updatedArray = [...currentArray];
      updatedArray[index] = updatedItem;
      return {
        ...prev,
        [field]: updatedArray
      };
    });
  };

  const addComplexArrayField = (field, item) => {
    setCourseData(prev => {
      const currentArray = Array.isArray(prev[field]) ? [...prev[field]] : [];
      return {
        ...prev,
        [field]: [...currentArray, item]
      };
    });
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const safeJoinArray = (array) => {
    if (!Array.isArray(array)) return 'string';
    
    const uniqueItems = [...new Set(
      array
        .filter(item => item && typeof item === 'string' && item.trim())
        .map(item => item.trim())
    )];
    
    return uniqueItems.length > 0 ? uniqueItems.join(', ') : 'string';
  };

  // ✅ Helper function to combine original data with current data
  const combineExistingAndNewData = () => {
    console.log('🔄 Combining existing and new data for update...');
    
    // Start with the current course data
    const combinedData = {
      ...courseData,
      // Ensure all existing sections are included
      sections: [...(courseData.sections || [])]
    };
    
    console.log('📦 Combined data prepared for update:', combinedData);
    return combinedData;
  };

  // ✅ FIXED: Function to prepare ALL data for update - ARRAYS AS ARRAYS (not joined strings)
  const prepareCompleteUpdateData = () => {
    console.log('📦 Preparing COMPLETE update data (existing + new)...');
    console.log('📦 Current courseData:', courseData);
    
    const safeProcessArray = (array) => {
      if (!Array.isArray(array)) return [];
      
      return array
        .filter(item => {
          if (typeof item === 'string') return item && item.trim() && item !== 'string';
          if (typeof item === 'object' && item !== null) {
            return item.content ? item.content.trim() : 
                   item.toString().trim();
          }
          return false;
        })
        .map(item => {
          if (typeof item === 'string') return item.trim();
          if (typeof item === 'object' && item !== null) {
            return item.content ? item.content.trim() : 
                   item.toString().trim();
          }
          return String(item).trim();
        });
    };

    // ✅ Process Step 1 data - KEEP AS ARRAYS (don't join)
    const objectives = safeProcessArray(courseData.objectives);
    const requirements = safeProcessArray(courseData.requirements);
    const audiences = safeProcessArray(courseData.audiences);
    
    const categoryId = courseData.category ? parseInt(courseData.category) : 0;
    const subcategoryId = courseData.subCategory ? parseInt(courseData.subCategory) : 0;
    
    console.log('🔢 Update - Category ID:', categoryId, 'Type:', typeof categoryId);
    console.log('🔢 Update - Subcategory ID:', subcategoryId, 'Type:', typeof subcategoryId);
    console.log('📝 Update - Objectives (as array):', objectives);
    console.log('📝 Update - Requirements (as array):', requirements);
    console.log('📝 Update - Audiences (as array):', audiences);
    console.log('💰 Update - Price:', courseData.price);
    console.log('💰 Update - Promo Price:', courseData.promoPrice);
    console.log('🖼️ Update - Thumbnail File:', thumbnailFile);
    console.log('🖼️ Update - Original Thumbnail URL:', originalThumbnailUrl);
    console.log('🔄 Update - Is Edit Mode:', isEditMode);
    console.log('📊 Update - Current sections:', courseData.sections);
    console.log('📊 Update - Original sections:', originalSectionsData);
    console.log('🆔 Update - Course ID:', courseData.id || draftId);
    
    // Start with basic update data - ARRAYS AS ARRAYS (not joined strings)
    const updateData = {
      courseId: courseData.id || draftId,
      title: courseData.title || '',
      subtitle: courseData.subtitle || '',
      description: courseData.description || '',
      categoryId: categoryId,
      subcategoryId: subcategoryId,
      level: courseData.level || 'Beginner',
      language: courseData.language || 'English',
      price: courseData.price ? parseFloat(courseData.price) : 0,
      promoPrice: courseData.promoPrice ? parseFloat(courseData.promoPrice) : 0,
      // ✅ CRITICAL: Arrays as arrays (not joined strings)
      objectives: objectives,
      requirements: requirements,
      audiences: audiences,
    };

    console.log('📤 Update Data after adding Step 1 (as arrays):', {
      ...updateData,
      objectives: updateData.objectives,
      requirements: updateData.requirements,
      audiences: updateData.audiences
    });

    // ✅ Handle thumbnail update properly for EDIT mode
    if (isEditMode) {
      console.log('🔄 EDIT MODE UPDATE: Processing thumbnail...');
      
      // PRIORITY 1: New file uploaded
      if (thumbnailFile && thumbnailFile instanceof File) {
        console.log('🖼️ EDIT UPDATE: Using new thumbnail file');
        updateData.thumbnail = thumbnailFile;
      } 
      // PRIORITY 2: Existing thumbnail URL from courseData
      else if (courseData.thumbnail && typeof courseData.thumbnail === 'string' && 
               courseData.thumbnail.startsWith('http')) {
        console.log('🖼️ EDIT UPDATE: Using thumbnail from courseData:', courseData.thumbnail);
        updateData.thumbnailUrl = courseData.thumbnail;
      }
      // PRIORITY 3: Original thumbnail URL from API
      else if (originalThumbnailUrl) {
        console.log('🖼️ EDIT UPDATE: Using original thumbnail URL:', originalThumbnailUrl);
        updateData.thumbnailUrl = originalThumbnailUrl;
      }
      else {
        console.log('⚠️ EDIT UPDATE: No thumbnail data available');
      }
    } else {
      // For non-edit mode (shouldn't happen, but just in case)
      if (thumbnailFile && thumbnailFile instanceof File) {
        updateData.thumbnail = thumbnailFile;
      }
    }

    // ✅ Include ALL sections data for update with quizzes
    console.log('📂 EDIT UPDATE: Preparing COMPLETE sections data with quizzes...');
    
    // Create a function to process sections for API
    const processSectionForApi = (section) => {
      const sectionData = {
        id: section.serverSectionId || section.id,
        name: section.name || '',
        items: []
      };

      // Process videos
      if (section.videos && Array.isArray(section.videos)) {
        section.videos.forEach(video => {
          const videoItem = {
            id: video.serverVideoId || video.id,
            type: 'VIDEO',
            videoId: video.serverVideoId || video.id,
            title: video.title || '',
            description: video.description || '',
            vimeoUrl: video.vimeoUrl || '',
            duration: video.duration || 0,
            isLocked: video.isLocked || false,
            uploadStatus: video.uploadStatus || 'success'
          };
          sectionData.items.push(videoItem);
        });
      }

      // Process PDFs
      if (section.pdfs && Array.isArray(section.pdfs)) {
        section.pdfs.forEach(pdf => {
          const pdfItem = {
            id: pdf.serverPdfId || pdf.id,
            type: 'PDF',
            pdfId: pdf.serverPdfId || pdf.id,
            title: pdf.title || '',
            fileUrl: pdf.serverPdfUrl || '',
            isLocked: pdf.isLocked || false,
            uploadStatus: 'success'
          };
          sectionData.items.push(pdfItem);
        });
      }

      // Process quizzes
      if (section.quizzes && Array.isArray(section.quizzes)) {
        section.quizzes.forEach(quiz => {
          const quizItem = {
            id: quiz.id,
            type: 'QUIZ',
            quizId: quiz.id,
            title: quiz.title || '',
            isLocked: quiz.isLocked || false,
            questions: quiz.questions || []
          };
          sectionData.items.push(quizItem);
        });
      }

      // Process nested sections
      if (section.nestedSections && Array.isArray(section.nestedSections)) {
        sectionData.children = section.nestedSections.map(childSection => 
          processSectionForApi(childSection)
        );
      }

      return sectionData;
    };

    // Process all sections
    if (courseData.sections && Array.isArray(courseData.sections)) {
      const processedSections = courseData.sections.map(section => 
        processSectionForApi(section)
      );
      
      updateData.sections = JSON.stringify(processedSections);
      console.log('📂 COMPLETE sections data for update:', processedSections);
      console.log('📂 Sections data length:', processedSections.length);
      if (processedSections.length > 0) {
        console.log('📂 First section details:', processedSections[0]);
      }
    } else {
      console.log('⚠️ No sections data found in courseData');
      // If no sections in courseData but we have original sections, preserve them
      if (originalSectionsData && originalSectionsData.length > 0) {
        console.log('📂 Using original sections data:', originalSectionsData);
        updateData.sections = JSON.stringify(originalSectionsData);
      } else {
        updateData.sections = JSON.stringify([]);
      }
    }

    console.log('📤 COMPLETE Update Course Data (with arrays):', {
      ...updateData,
      objectives: updateData.objectives,
      requirements: updateData.requirements,
      audiences: updateData.audiences,
      sections: updateData.sections ? '[JSON data]' : '[]'
    });
    
    return updateData;
  };

  const prepareApiData = (step) => {
    const apiStepId = getApiStepId(step);
    
    console.log(`📋 Preparing data for step ${apiStepId} (UI step ${step})`);
    console.log('🔍 Current course data:', courseData);
    console.log('📎 Thumbnail File:', thumbnailFile);
    console.log('🖼️ Original Thumbnail URL:', originalThumbnailUrl);
    console.log('🖼️ Course Data Thumbnail:', courseData.thumbnail);
    console.log('✏️ Is Edit Mode:', isEditMode);
    
    const categoryId = courseData.category ? parseInt(courseData.category) : 0;
    const subcategoryId = courseData.subCategory ? parseInt(courseData.subCategory) : 0;
    
    console.log('🔢 Category ID:', categoryId, 'Type:', typeof categoryId);
    console.log('🔢 Subcategory ID:', subcategoryId, 'Type:', typeof subcategoryId);
    console.log('🔢 Level:', courseData.level);
    
    const baseData = {
      title: courseData.title || 'string',
      subtitle: courseData.subtitle || 'string',
      description: courseData.description || 'string',
      categoryId: categoryId,
      subcategoryId: subcategoryId,
      level: courseData.level || 'string',
      language: courseData.language || 'string',
    };
    
    if (apiStepId >= 1) {
      console.log('🔍 Raw objectives before processing:', courseData.objectives);
      console.log('🔍 Raw requirements before processing:', courseData.requirements);
      console.log('🔍 Raw audiences before processing:', courseData.audiences);
      
      baseData.objectives = safeJoinArray(courseData.objectives);
      baseData.requirements = safeJoinArray(courseData.requirements);
      baseData.audiences = safeJoinArray(courseData.audiences);
      
      console.log('🧹 Processed objectives:', baseData.objectives);
      console.log('🧹 Processed requirements:', baseData.requirements);
      console.log('🧹 Processed audiences:', baseData.audiences);
    }
    
    if (apiStepId >= 2) {
      baseData.price = courseData.price ? parseFloat(courseData.price) : 0;
      baseData.promoPrice = courseData.promoPrice ? parseFloat(courseData.promoPrice) : 0;
      
      // ✅ Handle thumbnail properly
      if (thumbnailFile && thumbnailFile instanceof File) {
        console.log('🖼️ User uploaded new thumbnail file');
        baseData.thumbnail = thumbnailFile;
      } 
      else if (courseData.thumbnail && typeof courseData.thumbnail === 'string') {
        console.log('🖼️ Using existing thumbnail URL:', courseData.thumbnail);
        baseData.thumbnail = courseData.thumbnail;
      }
      else if (originalThumbnailUrl) {
        console.log('🖼️ Using original thumbnail URL:', originalThumbnailUrl);
        baseData.thumbnail = originalThumbnailUrl;
      }
      else {
        console.log('⚠️ No thumbnail available');
        baseData.thumbnail = null;
      }
      
      console.log('🖼️ Final thumbnail data:', baseData.thumbnail ? 'Present' : 'Missing');
    }
    
    // ✅ FIXED: In edit mode, always use the existing course ID, don't send courseId in step data
    if (!isEditMode) {
      if (draftId) {
        baseData.courseId = draftId.toString();
      } else if (apiStepId > 0) {
        baseData.courseId = '0';
      }
    }
    
    console.log(`📦 Prepared API data for step ${apiStepId}:`, baseData);
    return baseData;
  };

  const submitStepToApi = async (step) => {
    try {
      // ✅ FIXED: In edit mode, don't submit steps to createCourse
      if (isEditMode) {
        console.log('⏭️ Edit mode - skipping step submission to createCourse');
        return null;
      }
      
      if (submittedSteps.has(step)) {
        console.log(`⏭️ Step ${step} already submitted, skipping`);
        return null;
      }

      setApiError(null);
      setIsSubmitting(true);
      
      const apiStepId = getApiStepId(step);
      const apiData = prepareApiData(step);
      
      console.log(`📤 Submitting step ${apiStepId} (UI step ${step}) with data:`, apiData);
      console.log(`🆔 Course ID for step ${apiStepId}:`, draftId);
      console.log(`✏️ Edit mode:`, isEditMode);
      console.log(`🖼️ Thumbnail in API data:`, apiData.thumbnail ? 'Present' : 'Missing');
      
      // ✅ Create FormData for file upload
      const formData = new FormData();
      
      // ✅ Add all data to FormData
      Object.keys(apiData).forEach(key => {
        if (key === 'thumbnail' && apiData[key]) {
          if (apiData[key] instanceof File) {
            formData.append('thumbnail', apiData[key]);
            console.log('📤 Appending thumbnail as file');
          } 
          else if (typeof apiData[key] === 'string') {
            formData.append('thumbnail', apiData[key]);
            console.log('📤 Appending thumbnail as URL string:', apiData[key]);
          }
        } else {
          formData.append(key, apiData[key]);
        }
      });
      
      console.log('📋 FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ', pair[1]);
      }

      const filteredApiData = {
        ...apiData,
        objectives: apiData.objectives ? apiData.objectives.split(', ')
          .filter(item => item && item.trim() && item !== 'string')
          .join(', ') : '',
        requirements: apiData.requirements ? apiData.requirements.split(', ')
          .filter(item => item && item.trim() && item !== 'string')
          .join(', ') : '',
        audiences: apiData.audiences ? apiData.audiences.split(', ')
          .filter(item => item && item.trim() && item !== 'string')
          .join(', ') : ''
      };
      
      console.log('🧹 Filtered API data:', filteredApiData);

      const result = await createCourse({
        stepId: apiStepId,
        data: filteredApiData,
      }).unwrap();

      console.log('✅ API Response:', result);
      
      setSubmittedSteps(prev => new Set(prev).add(step));
      
      if (apiStepId === 0 && result.course?.id) {
        const newDraftId = result.course.id;
        setDraftId(newDraftId);
        
        setCourseData(prev => ({
          ...prev,
          id: newDraftId,
          courseId: newDraftId
        }));
        
        if (!isEditMode) {
          const updatedDraft = {
            ...courseData,
            id: newDraftId
          };
          localStorage.setItem('courseDraft', JSON.stringify(updatedDraft));
        }
      }
      
      setApiSuccess(result.message || 'Step completed successfully!');
      
      setTimeout(() => setApiSuccess(null), 3000);
      
      return result;
      
    } catch (error) {
      console.error('❌ API Error:', error);
      console.error('📋 API Error Details:', error.data);
      
      let errorMessage = 'An error occurred while saving. Please try again.';
      
      if (error.status === 400) {
        if (error.data?.message) {
          errorMessage = `Validation error: ${error.data.message}`;
        } else if (error.data?.errors) {
          const validationErrors = error.data.errors.map(err => err.msg || err.message).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else {
          errorMessage = 'Validation error: Please check all required fields are filled correctly.';
        }
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 404) {
        errorMessage = 'Course not found. Please start over.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      setApiError(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateCourseData = async () => {
    try {
      setApiError(null);
      setIsSubmitting(true);
      
      if (!courseData.id && !draftId) {
        throw new Error('Course ID is missing. Cannot update course.');
      }
      
      // ✅ Use the COMPLETE update data preparation function
      const updateData = prepareCompleteUpdateData();
      
      console.log('🔄 Updating course with COMPLETE data (existing + new):', updateData);

      const result = await updateCourse(updateData).unwrap();

      console.log('✅ Update Course API Response:', result);
      
      setApiSuccess(result.message || 'Course updated successfully! Changes are pending admin approval.');
      setShowUpdateSuccess(true);
      
      // Refresh course data after update
      if (draftId) {
        setTimeout(() => {
          refetch();
        }, 1000);
      }
      
      setTimeout(() => {
        setApiSuccess(null);
        setShowUpdateSuccess(false);
      }, 5000);
      
      return result;
      
    } catch (error) {
      console.error('❌ Update Course API Error:', error);
      console.error('📋 Update Course API Error Details:', error.data);
      
      let errorMessage = 'An error occurred while updating the course. Please try again.';
      
      if (error.status === 400) {
        if (error.data?.message) {
          errorMessage = `Validation error: ${error.data.message}`;
        } else {
          errorMessage = 'Validation error: Please check all required fields are filled correctly.';
        }
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 403) {
        errorMessage = 'You can only update your own courses.';
      } else if (error.status === 404) {
        errorMessage = 'Course not found. Please check the course ID.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      setApiError(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const publishCourseForApproval = async () => {
    try {
      setApiError(null);
      setIsSubmitting(true);
      
      if (!draftId) {
        throw new Error('Course ID is missing. Please complete all previous steps.');
      }
      
      console.log(`🚀 Publishing course with ID: ${draftId}`);
      console.log(`✏️ Edit mode:`, isEditMode);
      
      const result = await publishCourse(draftId).unwrap();

      console.log('✅ Publish API Response:', result);
      
      setApiSuccess(result.message || 'Course submitted for review successfully!');
      setShowPublishSuccess(true);
      
      if (!isEditMode) {
        localStorage.removeItem('courseDraft');
        localStorage.removeItem('courseDrafts');
      }
      
      setTimeout(() => {
        setApiSuccess(null);
        setShowPublishSuccess(false);
      }, 5000);
      
      return result;
      
    } catch (error) {
      console.error('❌ Publish API Error:', error);
      console.error('📋 Publish API Error Details:', error.data);
      
      let errorMessage = 'An error occurred while submitting for approval. Please try again.';
      
      if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 404) {
        errorMessage = 'Course not found. Please complete all steps first.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      setApiError(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveAsDraft = async () => {
    try {
      setApiError(null);
      setIsSubmitting(true);
      
      if (isEditMode) {
        await updateCourseData();
        return;
      }
      
      await submitStepToApi(currentStep);
      setShowSaveSuccess(true);
      
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalSubmit = async () => {
    try {
      if (isEditMode) {
        await updateCourseData();
      } else {
        for (let step = 1; step <= 3; step++) {
          try {
            await submitStepToApi(step);
          } catch (error) {
            console.warn(`Step ${step} submission had issues:`, error);
          }
        }
      }
      
      await publishCourseForApproval();
      
      setTimeout(() => {
        navigate('/courses');
      }, 2000);
      
    } catch (error) {
      console.error('Final submission error:', error);
    }
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem('courseDraft');
    setCourseData(initialCourseData);
    setDraftId(null);
    setShowDraftModal(false);
    setThumbnailFile(null);
    setOriginalThumbnailUrl(null);
    setOriginalCourseData(null);
    setOriginalSectionsData([]);
  };

  const handleContinueDraft = () => {
    setShowDraftModal(false);
  };

  const handleNextStep = async () => {
    try {
      // ✅ FIXED: In edit mode, don't submit steps
      if (isEditMode) {
        console.log('⏭️ Edit mode - moving to next step without API call');
        if (currentStep < steps.length) {
          setCurrentStep(currentStep + 1);
        }
        return;
      }
      
      if (currentStep <= 3) {
        await submitStepToApi(currentStep);
      }
      
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.log('❌ API submission failed, staying on current step');
    }
  };

  const renderStep = () => {
    const safeCourseData = {
      ...courseData,
      sections: courseData.sections || []
    };

    const commonProps = {
      courseData: safeCourseData,
      onInputChange: handleInputChange,
      onArrayFieldChange: handleArrayFieldChange,
      onAddArrayField: addArrayField,
      onRemoveArrayField: removeArrayField,
      onFileUpload: handleFileUpload,
      onComplexArrayFieldChange: handleComplexArrayFieldChange,
      onAddComplexArrayField: addComplexArrayField,
      isEditMode
    };

    switch (currentStep) {
      case 1:
        return <BasicInfoStep {...commonProps} />;
      case 2:
        return <ContentStep {...commonProps} />;
      case 3:
        return <MediaPricingStep 
          {...commonProps} 
          onThumbnailChange={handleThumbnailChange}
          refetchCourse={refetch}
        />;
      case 4:
        return <SettingsStep {...commonProps} />;
      default:
        return <BasicInfoStep {...commonProps} />;
    }
  };

  if (isEditMode && isCourseDetailLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading course data...</p>
        </div>
      </div>
    );
  }

  if (isEditMode && isCourseDetailError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 text-lg font-semibold mb-2">
          Error Loading Course
        </div>
        <p className="text-red-700 mb-4">
          {courseDetailError?.data?.message || 'Failed to load course data. Please try again.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Course' : 'Create New Course'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEditMode 
              ? 'Update your course information and submit for review' 
              : 'Fill in the details to create your amazing course'
            }
          </p>
        </div>

        <ProgressSteps 
          steps={steps} 
          currentStep={currentStep} 
        />

        {apiError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {apiError}
                </p>
              </div>
            </div>
          </div>
        )}

        {apiSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Success
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  {apiSuccess}
                </p>
              </div>
            </div>
          </div>
        )}

        {showSaveSuccess && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  {isEditMode ? 'Course updated successfully!' : 'Draft saved successfully!'}
                </p>
              </div>
            </div>
          </div>
        )}

        {showPublishSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Course Submitted for Review!</h3>
                <p className="text-gray-600 mb-6">
                  Your course has been successfully submitted for admin approval. 
                  You will be notified once it's reviewed and published.
                </p>
                <button
                  onClick={() => {
                    setShowPublishSuccess(false);
                    navigate('/courses');
                  }}
                  className="w-full px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all duration-300"
                >
                  Back to Courses
                </button>
              </div>
            </div>
          </div>
        )}

        {showUpdateSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  Course updated successfully! Changes are pending admin approval.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleFinalSubmit();
          }}>
            {renderStep()}

            <div className="border-t border-gray-200 px-8 py-6">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <div className="flex space-x-3 w-full sm:w-auto">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      disabled={isSubmitting}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold transition-all duration-300 hover:bg-gray-50 hover:scale-105 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                  )}
                  
                  <button
                    type="button"
                    onClick={saveAsDraft}
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold transition-all duration-300 hover:bg-blue-600 hover:scale-105 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Course' : 'Save as Draft')}
                  </button>
                </div>

                <div className="w-full sm:w-auto">
                  {currentStep < steps.length ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      disabled={isSubmitting}
                      className="px-8 py-3 bg-green-500 text-white rounded-xl font-semibold transition-all duration-300 hover:bg-green-600 hover:scale-105 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Saving...' : (isEditMode ? 'Next' : 'Next')}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`px-8 py-3 text-white rounded-xl font-semibold transition-all duration-300 w-full sm:w-auto ${
                        isSubmitting 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-green-500 hover:bg-green-600 hover:scale-105'
                      }`}
                    >
                      {isSubmitting ? 'Submitting...' : (isEditMode ? 'Update & Submit for Review' : 'Submit for Approval')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showDraftModal && !isEditMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Continue Your Draft?</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  We found an unpublished course draft. Would you like to continue where you left off?
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleDiscardDraft}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300"
                >
                  Start Fresh
                </button>
                <button
                  onClick={handleContinueDraft}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300"
                >
                  Continue Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCourse;